import React, { useEffect, useMemo, useState, useCallback } from "react";
import { CheckCircle2, BookOpenCheck } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import { supabase } from "../../Services/supabaseClient";
import {
  getPayrollRuns,
  getApprovals,
  approvePayrollRun,
  ensurePayrollJournalBootstrap,
  validatePayrollJournalMappings,
  postPayrollToJournal,
} from "../payrollService";

function pickOne(value) {
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
}

export default function PayrollApprovals() {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState("");
  const [runs, setRuns] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [actingRunId, setActingRunId] = useState("");
  const [postingRunId, setPostingRunId] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      await ensurePayrollJournalBootstrap(companyId);

      const [runsData, approvalsData] = await Promise.all([
        getPayrollRuns(companyId),
        getApprovals(companyId),
      ]);

      setRuns(runsData || []);
      setApprovals(approvalsData || []);
    } catch (err) {
      setMsg(err.message || "Failed to load approvals data");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingRuns = useMemo(
    () => runs.filter((x) => x.status === "completed"),
    [runs]
  );

  const approvedRuns = useMemo(
    () => runs.filter((x) => x.status === "approved"),
    [runs]
  );

  async function handleApprove(run) {
    try {
      setMsg("");
      setActingRunId(run.id);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user?.id) {
        throw new Error("No authenticated user found");
      }

      await approvePayrollRun(
        companyId,
        run.id,
        user.id,
        "Approved from payroll approvals page"
      );

      await load();
      setMsg(`Payroll run "${run.run_name}" approved successfully.`);
    } catch (err) {
      setMsg(err.message || "Failed to approve payroll run");
    } finally {
      setActingRunId("");
    }
  }

  async function handlePostToJournal(run) {
    try {
      setMsg("");
      setPostingRunId(run.id);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user?.id) {
        throw new Error("No authenticated user found");
      }

      const mappings = await validatePayrollJournalMappings(companyId);
      const missing = (mappings || []).filter((x) => !x.is_configured);

      if (missing.length > 0) {
        const names = missing.map((x) => x.mapping_name).join(", ");
        throw new Error(
          `Payroll journal account mappings are incomplete. Missing: ${names}`
        );
      }

      await postPayrollToJournal({
        companyId,
        payrollRunId: run.id,
        createdBy: user.id,
        postingDate: new Date().toISOString().slice(0, 10),
        notes: "Posted from payroll approvals page",
      });

      await load();
      setMsg(`Payroll run "${run.run_name}" posted to journal successfully.`);
    } catch (err) {
      console.error("handlePostToJournal error:", err);
      setMsg(err.message || "Failed to post payroll to journal");
    } finally {
      setPostingRunId("");
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Awaiting Approval</p>
          <h3>{pendingRuns.length}</h3>
          <span>Processed runs pending approval</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Approved Runs</p>
          <h3>{approvedRuns.length}</h3>
          <span>Runs already approved</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Approval Records</p>
          <h3>{approvals.length}</h3>
          <span>Workflow records created</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Journal Posted</p>
          <h3>{runs.filter((x) => x.journal_posted).length}</h3>
          <span>Approved runs posted to accounting</span>
        </div>
      </div>

      <div className="payroll-grid payroll-grid-2">
        <div className="payroll-card">
          <div className="payroll-card-header">
            <div>
              <h3>Runs Pending Approval</h3>
              <p>
                Review processed payroll runs and approve them for posting and
                payment.
              </p>
            </div>
          </div>

          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead>
                <tr>
                  <th>Run Name</th>
                  <th>Period</th>
                  <th>Employees</th>
                  <th>Gross</th>
                  <th>Net</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingRuns.map((row) => {
                  const period = pickOne(row.payroll_periods);

                  return (
                    <tr key={row.id}>
                      <td>{row.run_name}</td>
                      <td>{period?.period_name || "-"}</td>
                      <td>
                        {Number(row.employee_count || 0).toLocaleString()}
                      </td>
                      <td>{Number(row.gross_total || 0).toLocaleString()}</td>
                      <td>{Number(row.net_total || 0).toLocaleString()}</td>
                      <td>
                        <span className={`pill ${row.status}`}>
                          {row.status}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="primary-btn"
                          onClick={() => handleApprove(row)}
                          disabled={actingRunId === row.id}
                        >
                          <CheckCircle2 size={16} />
                          {actingRunId === row.id ? "Approving..." : "Approve"}
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {!pendingRuns.length && !busy && (
                  <tr>
                    <td colSpan="7">No payroll runs pending approval.</td>
                  </tr>
                )}

                {busy && (
                  <tr>
                    <td colSpan="7">Loading...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="payroll-card">
          <div className="payroll-card-header">
            <div>
              <h3>Approved Runs Ready for Journal Posting</h3>
              <p>
                Post approved payroll runs into journal entries for accounting.
              </p>
            </div>
          </div>

          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead>
                <tr>
                  <th>Run Name</th>
                  <th>Gross</th>
                  <th>Net</th>
                  <th>Journal Status</th>
                  <th>Posted At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {approvedRuns.map((row) => (
                  <tr key={row.id}>
                    <td>{row.run_name}</td>
                    <td>{Number(row.gross_total || 0).toLocaleString()}</td>
                    <td>{Number(row.net_total || 0).toLocaleString()}</td>
                    <td>
                      <span
                        className={`pill ${
                          row.payroll_journal_status ||
                          (row.journal_posted ? "posted" : "not_posted")
                        }`}
                      >
                        {row.payroll_journal_status ||
                          (row.journal_posted ? "posted" : "not_posted")}
                      </span>
                    </td>
                    <td>
                      {row.journal_posted_at
                        ? new Date(row.journal_posted_at).toLocaleString()
                        : "-"}
                    </td>
                    <td>
                      {row.journal_posted ? (
                        <span className="pill approved">posted</span>
                      ) : (
                        <button
                          type="button"
                          className="primary-btn"
                          onClick={() => handlePostToJournal(row)}
                          disabled={postingRunId === row.id}
                        >
                          <BookOpenCheck size={16} />
                          {postingRunId === row.id
                            ? "Posting..."
                            : "Post to Journal"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {!approvedRuns.length && !busy && (
                  <tr>
                    <td colSpan="6">No approved payroll runs available.</td>
                  </tr>
                )}

                {busy && (
                  <tr>
                    <td colSpan="6">Loading...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Approval History</h3>
            <p>View approval workflow records for payroll runs.</p>
          </div>
        </div>

        <div className="payroll-table-wrap">
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Run</th>
                <th>Step</th>
                <th>Status</th>
                <th>Action Time</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {approvals.map((row) => {
                const run = pickOne(row.payroll_runs);
                const step = pickOne(row.payroll_approval_steps);

                return (
                  <tr key={row.id}>
                    <td>{run?.run_name || "-"}</td>
                    <td>{step?.step_name || "-"}</td>
                    <td>
                      <span className={`pill ${row.status}`}>{row.status}</span>
                    </td>
                    <td>
                      {row.action_at
                        ? new Date(row.action_at).toLocaleString()
                        : "-"}
                    </td>
                    <td>{row.notes || "-"}</td>
                  </tr>
                );
              })}

              {!approvals.length && !busy && (
                <tr>
                  <td colSpan="5">No approval records found.</td>
                </tr>
              )}

              {busy && (
                <tr>
                  <td colSpan="5">Loading...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
